'use client'

import * as React from 'react'
import qs from 'qs'
import type { UiState } from 'instantsearch.js'
import type { InstantSearchNextRouting } from 'react-instantsearch-nextjs'
import { InstantSearchNext } from 'react-instantsearch-nextjs'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'
import { defaultSearchClient } from 'algolia/algolia'
import { InsightsMiddleware } from 'algolia/insights-middleware'
import type { RouteType } from '@repo/studio-components-templates/algolia/search-provider'
import { stateMapping } from '@repo/studio-components-templates/algolia/search-provider'
import { urlUpdateFallback } from 'algolia/algolia.utils'
import { AppRoutes } from '@repo/web-utils/app-routes'

const CATEGORY_PATHNAME_REGEX = new RegExp(`${AppRoutes.STUDIO_TEMPLATES}(.*?)/?$`)
const BASE_URL_REGEX = new RegExp(`^(.*?)${AppRoutes.STUDIO_TEMPLATES}`)

const routing: InstantSearchNextRouting<UiState, RouteType | null> = {
  router: {
    parseURL({ location }) {
      if (location.pathname.startsWith(AppRoutes.STUDIO_TEMPLATES_ITEM)) {
        return null
      }

      return {
        pathname: location.pathname.match(CATEGORY_PATHNAME_REGEX)?.[1] || '',
        query: location.search.slice(1),
      }
    },
    createURL({ routeState, location }) {
      const { pathname, query } = routeState || {}
      const baseUrl = location.href.match(BASE_URL_REGEX)?.[1]

      return `${baseUrl}${AppRoutes.STUDIO_TEMPLATES}${pathname ? `${pathname}/` : ''}${
        query ? `?${query}` : ''
      }`
    },
    push(url) {
      // If the user is on the item route it means the modal is open, so we don't want to push a new url
      if (window.location.pathname.startsWith(AppRoutes.STUDIO_TEMPLATES_ITEM)) {
        return
      }
      window.history.pushState({}, '', url)
    },
  },
  stateMapping,
}

export const TemplatesSearchProvider = ({
  withRouting = false,
  children,
}: {
  withRouting?: boolean
  children?: React.ReactNode
}) => {
  return (
    <InstantSearchNext
      future={{ preserveSharedStateOnUnmount: true }}
      searchClient={defaultSearchClient}
      indexName={algoliaTemplatesIndex}
      routing={withRouting ? routing : undefined}
      onStateChange={({ setUiState, uiState }) => {
        setUiState(uiState)

        const url = routing?.router?.createURL?.({
          routeState: routing.stateMapping?.stateToRoute(uiState) || null,
          location: window.location,
          qsModule: qs,
        })
        urlUpdateFallback(AppRoutes.STUDIO_TEMPLATES_ITEM, url)
      }}
    >
      {children}
      <InsightsMiddleware />
    </InstantSearchNext>
  )
}
